import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as semaphore from 'semaphore';
import SemaphoreHelper from '../../helpers/semaphore-helper';
import { CollectionHelper } from '../../helpers/collection-helper';

@Injectable({
  providedIn: 'root'
})
export class SubmitEventService {
  private readonly localStorageKey = 'submit-in-progress';
  private readonly submitInProgressSemaphore = semaphore(1);

  private getSubmitInProgress(): SubmitInProgressEntry[] {
    const toReturn = localStorage.getItem(this.localStorageKey);
    return toReturn == null ? [] : JSON.parse(toReturn);
  }

  public getSubmitInProgressAsync(): Promise<SubmitInProgressEntry[]> {
    return new Promise((resolve, reject) => {
      SemaphoreHelper.use(this.submitInProgressSemaphore, async () => {
        resolve(this.getSubmitInProgress());
      });
    });
  }

  public isInProgress(
    id: string,
    type: SubmitInProgressType
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      SemaphoreHelper.use(this.submitInProgressSemaphore, async () => {
        const inProgress = this.getSubmitInProgress();
        resolve(
          inProgress != null &&
            inProgress.some(p => p.id === id && p.type === type)
        );
      });
    });
  }

  public setSubmitInProgress(
    id: string,
    type: SubmitInProgressType
  ): Promise<void> {
    return SemaphoreHelper.use(this.submitInProgressSemaphore, async () => {
      const inProgress = this.getSubmitInProgress();
      inProgress.push({ id: id, type: type });
      localStorage.setItem(this.localStorageKey, JSON.stringify(inProgress));
      this.submitInProgressChanged.next();
    });
  }

  public removeSubmitInProgress(
    id: string,
    type: SubmitInProgressType
  ): Promise<void> {
    return SemaphoreHelper.use(this.submitInProgressSemaphore, async () => {
      const inProgress = this.getSubmitInProgress();
      CollectionHelper.filterInPlace(
        inProgress,
        i => i.id !== id && i.type !== type
      );
      localStorage.setItem(this.localStorageKey, JSON.stringify(inProgress));
      this.submitInProgressChanged.next();
    });
  }

  private readonly submitInProgressChanged = new Subject<any>();

  public getSubmitInProgressChangedSubscribable(): Subject<any> {
    return this.submitInProgressChanged;
  }
}

export enum SubmitInProgressType {
  SUB_PROJECT,
  TASK
}

export interface SubmitInProgressEntry {
  id: string;
  type: SubmitInProgressType;
}
